import {
  BundleType,
  Deal,
  type OrderValues,
  RemoteConfig,
  StrapiID,
} from '@ca/common/types'
import AppConfig from 'config/AppConfig'
import { getRemoteConfig, useRemoteConfig } from 'hooks/useRemoteConfigStore'
import useSession, { DealCtxKeys, DealsContext } from 'hooks/useSession'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useBundleType from './useBundleType'
import i18n from 'config/i18n'
import { validateItem } from '@order/utils/validateItem'

export const getDealLocaleKey = (id: StrapiID, locale: string) =>
  `${id}:${locale}`

export const getDealsByIdFromConfig = (
  deals: RemoteConfig['deals'] | undefined
) => {
  if (!deals) {
    return {}
  }
  const allArr = Object.values(deals).flat()
  const result: Record<StrapiID, Deal> = {}
  allArr.forEach((deal) => {
    result[deal.id] = deal
    result[getDealLocaleKey(deal.id, deal.locale)] = deal
  })
  return result
}

export const getDealsById = () => {
  const config = getRemoteConfig()
  return getDealsByIdFromConfig(config?.deals)
}

export const useDealsById = (): Record<StrapiID, Deal> => {
  const config = useRemoteConfig()

  return useMemo(() => {
    return getDealsByIdFromConfig(config?.deals)
  }, [config?.deals])
}

export const getLocalizedDealById = (
  id: StrapiID | undefined,
  locale: string,
  dealsById: Record<StrapiID, Deal>
) => {
  if (!id) {
    return undefined
  }
  const source = dealsById[id]
  if (!source) {
    return undefined
  }
  if (source.locale === locale) {
    return source
  }
  // When documentId is shared across locales (Strapi v5+), look up by id:locale composite key
  const byLocale = dealsById[getDealLocaleKey(id, locale)]
  if (byLocale) {
    return byLocale
  }
  // Fall back to legacy per-locale pointer
  const targetId = source.localizations?.[locale]
  if (!targetId) {
    return undefined
  }
  return dealsById[targetId]
}

export const useDeal = (id?: StrapiID): Deal | undefined => {
  const byId = useDealsById()
  const { i18n } = useTranslation()
  const dealLang = AppConfig.dealLang || i18n.language

  const deal = useMemo(() => {
    return getLocalizedDealById(id, dealLang, byId)
  }, [id, dealLang, byId])
  return deal
}

export const resolveDealById = (id?: StrapiID) => {
  if (!id) {
    return undefined
  }
  const config = getRemoteConfig()
  if (!config) {
    return undefined
  }
  const dealLang = AppConfig.dealLang || i18n.language

  const byId = getDealsByIdFromConfig(config.deals)
  return getLocalizedDealById(id, dealLang, byId)
}

export const resolveSessionDeal = (
  bundleType: BundleType,
  ctx: DealsContext
) => {
  const keys = DealCtxKeys[bundleType]
  return ctx[keys.upgrade] || ctx[keys.base]
}

export const useSessionDealByBundleType = ({
  bundleType,
}: {
  bundleType: BundleType
}) => {
  const dealsState = useSession((state) => state.deals)

  return resolveSessionDeal(bundleType, dealsState)
}

export const useActiveDeal = () => {
  const bundle = useBundleType()
  const dealCtx = useSessionDealByBundleType(bundle)
  return useDeal(dealCtx?.dealId)
}

export const useBaseSessionDeal = () => {
  return useSessionDealByBundleType({ bundleType: 'base' })
}

export const useDealByBundleType = () => {
  const deals = useDealsById()
  const { i18n } = useTranslation()
  const baseCtx = useBaseSessionDeal()
  const extraCtx = useSessionDealByBundleType({ bundleType: 'extra' })

  const dealLang = AppConfig.dealLang || i18n.language

  return useMemo(
    () => ({
      base: getLocalizedDealById(baseCtx?.dealId, dealLang, deals),
      extra: getLocalizedDealById(extraCtx?.dealId, dealLang, deals),
    }),
    [deals, dealLang, baseCtx?.dealId, extraCtx?.dealId]
  )
}

export const getDealVariant = (
  deal: Deal,
  values: OrderValues,
  config: RemoteConfig | undefined | null
) => {
  const variant = deal.variants?.find((variant) => {
    return validateItem(
      {
        requires: variant.requires || null,
        // For now we don't validate the condition
        condition: null,
      },
      values.values,
      config?.conditions || {},
      values.refraction || undefined
    ).result
  })
  if (!variant) {
    return deal
  }

  return {
    ...deal,
    title: variant.title || deal.title,
    desc: variant.desc || deal.desc,
    body: variant.body || deal.body,
    image: variant.image || deal.image,
  }
}

export default useDeal
